/**
 * resultFilters — shared helpers for trimming a completed workflow's result
 * body before it renders in the drawer's Result section.
 *
 * Two concerns, both consumed only by WorkflowResultSection (the shared Result
 * panel for ora + the Outlook add-in):
 *
 *   1. `pruneResultKeys` — recursively drops verbose / sensitive keys that add
 *      no value in the Result panel (the evidence is in the Artifacts section,
 *      and the Graph token must never be rendered). See SKIP_KEYS.
 *   2. `isTruncatableBlob` — value-based predicate that flags a base64 blob
 *      (document/image bytes, the original .eml, attached-signature images) so
 *      the renderer can collapse it behind a "Show more" toggle instead of
 *      dumping ~80 KB of base64 inline.
 *
 * Verified against `carrier_mortgagee_update` result payloads (2026-06-03): the
 * long values live at `result.final_state.*` as base64 strings, sometimes as
 * unkeyed array elements (`draft_attached_sigs = [[name, b64]]`), which is why
 * truncation keys off the VALUE, not the field name.
 */

/**
 * Result keys removed from the rendered tree at ANY depth (case-insensitive).
 *
 * - `login_screenshots` / `captured_review_steps` / `api_evidence_steps` /
 *   `steps_reviewed`: evidence already reachable from the Artifacts section.
 * - `am_graph_access_token`: a live Microsoft Graph JWT that must never render.
 */
export const SKIP_KEYS: ReadonlySet<string> = new Set([
  "login_screenshots",
  "captured_review_steps",
  "api_evidence_steps",
  "steps_reviewed",
  "am_graph_access_token",
]);

/** True when `key` is one of the keys we strip from the Result tree. */
export function isSkippedResultKey(key: string): boolean {
  return SKIP_KEYS.has(key.toLowerCase());
}

/**
 * Return a deep copy of `value` with every SKIP_KEYS entry removed from every
 * nested object. Arrays are preserved (and their object elements pruned);
 * primitives pass through untouched. Pure — never mutates the input, never
 * throws on null / undefined / primitive input.
 */
export function pruneResultKeys(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => pruneResultKeys(v));
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (isSkippedResultKey(k)) continue;
      out[k] = pruneResultKeys(v);
    }
    return out;
  }
  return value;
}

/** Strings longer than this are candidates for base64-blob truncation. */
export const TRUNCATE_THRESHOLD = 200;

/**
 * Pure base64 alphabet (whitespace allowed for MIME line breaks). Anchored so a
 * single non-base64 char disqualifies the string — `<`/`>` in HTML and the `.`
 * separators in a JWT both fail, so readable values are never flagged.
 */
const BASE64_RE = /^[A-Za-z0-9+/=\s]+$/;

/**
 * True when `value` is a long base64-like blob worth collapsing behind a
 * "Show more" toggle (document/image bytes, the original .eml, signature
 * images). Value-based — independent of the field name — so it catches both
 * keyed (`bytes_b64`, `original_eml_b64`) and unkeyed (a `draft_attached_sigs`
 * element) blobs. Readable long strings (HTML, JWTs) are NOT flagged.
 */
export function isTruncatableBlob(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.length > TRUNCATE_THRESHOLD &&
    BASE64_RE.test(value)
  );
}
